/* eslint-disable no-console */
/**
 * Olipop-style restyle for the Shopify Subscriptions app block.
 *  - "Frequency: <selected>" eyebrow above the options
 *  - Short, bold option titles ("One time" / "Subscribe & Save") with a
 *    small frequency subtitle for the subscribe option
 *  - Discount % pill badge + strikethrough compare-at price
 *  - Strips the trailing currency code so prices read "$43.20" not "$43.20 NZD"
 */

function parsePrice(str) {
  if (!str) return null;
  const match = String(str).replace(/[^\d.,-]/g, '').replace(/,/g, '');
  const num = parseFloat(match);
  return Number.isFinite(num) ? num : null;
}

/** Strip a trailing 3-letter ISO currency code ("$43.20 NZD" -> "$43.20"). */
function stripCurrencyCode(str) {
  return String(str || '').replace(/\s*[A-Z]{3}\s*$/, '').trim();
}

/** First-delivery 20% offer: guests and customers who have never subscribed. */
function showFirstOrder20() {
  const node = document.getElementById('sk-first-sub-discount-config');
  if (!node) return false;
  try {
    const cfg = JSON.parse(node.textContent || '{}');
    return Boolean(cfg.enabled && cfg.isFirstSubscription !== false);
  } catch (_) {
    return false;
  }
}

function formatMoneyFromSample(sample, amount) {
  const raw = String(sample || '');
  const prefix = (raw.match(/^[^\d-]*/)?.[0] || '$').replace(/\s*[A-Z]{3}\s*$/, '');
  return `${prefix}${amount.toFixed(2)}`;
}

function setPriceText(priceWrapper, displayStr) {
  if (!priceWrapper) return;
  const priceText = ` ${displayStr} `;
  for (const node of priceWrapper.childNodes) {
    if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
      node.textContent = priceText;
      return;
    }
  }
  priceWrapper.insertBefore(document.createTextNode(priceText), priceWrapper.firstChild);
}

function shortTitle(input) {
  if (!input) return '';
  if (input.dataset.radioType === 'one_time_purchase') return 'One-time';
  return showFirstOrder20() ? 'Subscribe & save' : 'Subscribe & save 10%';
}

function ensureEyebrow(fieldset) {
  let eyebrow = fieldset.previousElementSibling;
  if (!(eyebrow instanceof HTMLElement) || !eyebrow.classList.contains('pdp-subscription-eyebrow')) {
    eyebrow = document.createElement('p');
    eyebrow.className = 'pdp-subscription-eyebrow';
    eyebrow.textContent = 'How would you like it?';
    fieldset.parentNode?.insertBefore(eyebrow, fieldset);
  } else if (!eyebrow.dataset.pdpHeadingLocked) {
    eyebrow.textContent = 'How would you like it?';
    eyebrow.dataset.pdpHeadingLocked = '1';
  }
  return eyebrow;
}

function updateEyebrow(fieldset) {
  // Heading is static; selection announced via native radio checked state.
  ensureEyebrow(fieldset);
}

/** Find the visible label span that holds the option's text. */
function getLabelSpan(input) {
  const label = input.closest('label');
  if (!label) return null;
  // One-time: span lives next to the input inside .group_list_item
  // Subscribe: span is the first span inside the inner .title_and_price_wrapper
  return (
    label.querySelector('.group_list_item > span') ||
    label.querySelector('.title_and_price_wrapper > span:not(.pdp-subscription-badge)') ||
    label.querySelector('span')
  );
}

function enhanceInput(input) {
  if (input.dataset.pdpEnhanced === '1') return;
  input.dataset.pdpEnhanced = '1';

  const isSubscribe = input.dataset.radioType === 'selling_plan';
  const labelSpan = getLabelSpan(input);

  if (labelSpan) {
    labelSpan.textContent = shortTitle(input);
    labelSpan.classList.add('pdp-subscription-title');
  }

  const firstOrder20 = isSubscribe && showFirstOrder20();

  // Subtitle (subscribe only).
  if (isSubscribe && labelSpan) {
    let sub = labelSpan.nextElementSibling;
    if (!(sub instanceof HTMLElement) || !sub.classList.contains('pdp-subscription-subtitle')) {
      sub = document.createElement('span');
      sub.className = 'pdp-subscription-subtitle';
      labelSpan.insertAdjacentElement('afterend', sub);
    }
    sub.textContent = firstOrder20
      ? '20% off your first delivery, then 10% · Skip or cancel anytime'
      : 'Delivered monthly · Skip or cancel anytime';
  }

  // Discount badge + strikethrough compare price.
  const compareRaw = input.dataset.variantCompareAtPrice;
  const priceRaw = input.dataset.variantPrice;
  const label = input.closest('label');
  const priceWrapper = label?.querySelector('.in_widget_price');
  const compareNum = parsePrice(compareRaw);
  const priceNum = parsePrice(priceRaw);
  const firstPriceNum =
    firstOrder20 && compareNum ? Math.round(compareNum * 0.8 * 100) / 100 : null;
  const displayPrice =
    firstPriceNum != null ? formatMoneyFromSample(priceRaw || compareRaw, firstPriceNum) : stripCurrencyCode(priceRaw);

  setPriceText(priceWrapper, displayPrice);

  if (compareRaw && priceRaw && compareRaw !== priceRaw) {
    if (priceWrapper && !priceWrapper.querySelector('.pdp-subscription-compare-price')) {
      const compareSpan = document.createElement('s');
      compareSpan.className = 'pdp-subscription-compare-price';
      compareSpan.textContent = stripCurrencyCode(compareRaw);
      priceWrapper.appendChild(compareSpan);
    }

    const displayNum = firstPriceNum != null ? firstPriceNum : priceNum;
    if (compareNum && displayNum && compareNum > displayNum && labelSpan) {
      const anchor = labelSpan.parentElement?.querySelector('.pdp-subscription-subtitle') || labelSpan;
      if (!label?.querySelector('.pdp-subscription-badge')) {
        const pct =
          firstPriceNum != null ? 20 : Math.round(((compareNum - priceNum) / compareNum) * 100);
        const badge = document.createElement('span');
        badge.className = 'pdp-subscription-badge';
        badge.textContent = `${pct}% Off`;
        anchor.insertAdjacentElement('afterend', badge);
      }
    }
  }
}

/**
 * Reorganise each option's inner row into two columns:
 *   [ title / subtitle ]      [ badge / price ]
 * so the radio, label and pricing line up cleanly — and prices clear the
 * selected-state check on mobile.
 *
 * One-time markup often puts the title next to the radio (outside a nested
 * wrapper); subscribe puts title + price inside .title_and_price_wrapper.
 */
function groupOptionRow(input) {
  const label = input.closest('label');
  if (!label || label.dataset.pdpGrouped === '1') return;

  const title = label.querySelector('.pdp-subscription-title');
  const price = label.querySelector('.in_widget_price');
  if (!title || !price) return;

  let wrapper = label.querySelector('.title_and_price_wrapper');
  if (!wrapper) {
    // One-time: promote the shared row into a flex wrapper after the radio.
    wrapper = document.createElement('div');
    wrapper.className = 'title_and_price_wrapper';
    const row = input.closest('.group_list_item') || label;
    row.appendChild(wrapper);
  }
  if (wrapper.dataset.pdpGrouped === '1') return;

  const subtitle = label.querySelector('.pdp-subscription-subtitle');
  const badge = label.querySelector('.pdp-subscription-badge');

  const textCol = document.createElement('div');
  textCol.className = 'pdp-subscription-textcol';
  textCol.appendChild(title);
  if (subtitle) textCol.appendChild(subtitle);

  const priceCol = document.createElement('div');
  priceCol.className = 'pdp-subscription-pricecol';
  if (badge) priceCol.appendChild(badge);
  priceCol.appendChild(price);

  wrapper.appendChild(textCol);
  wrapper.appendChild(priceCol);
  wrapper.dataset.pdpGrouped = '1';
  label.dataset.pdpGrouped = '1';
}

function enhance(fieldset) {
  if (fieldset.dataset.pdpRestyle === '1') return;
  fieldset.dataset.pdpRestyle = '1';

  // Hide the redundant standalone "Subscribe and Save" header — the radio
  // option itself now carries the "Subscribe & Save" title.
  fieldset.querySelectorAll('.subscription_group > .title_and_price_wrapper').forEach((wrapper) => {
    if (wrapper.querySelector('.group_name') && !wrapper.querySelector('input[type="radio"]')) {
      wrapper.classList.add('pdp-subscription-hidden-header');
    }
  });

  fieldset.querySelectorAll('input[type="radio"]').forEach(enhanceInput);

  // Lay out both options into text + price columns.
  fieldset.querySelectorAll('input[type="radio"]').forEach(groupOptionRow);

  ensureEyebrow(fieldset);
  updateEyebrow(fieldset);

  fieldset.addEventListener('change', (event) => {
    if (event.target instanceof HTMLInputElement && event.target.type === 'radio') {
      updateEyebrow(fieldset);
    }
  });
}

function isLimitedEditionPdp() {
  const body = document.body;
  if (!body) return false;
  if (body.classList.contains('pdp-no-subscription')) return true;
  if (body.classList.contains('product-limited-edition')) return true;
  return body.dataset.productHandle === 'the-sober-july-24-pack';
}

function scan() {
  if (isLimitedEditionPdp()) return;
  document.querySelectorAll('.shopify_subscriptions_fieldset').forEach(enhance);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scan);
} else {
  scan();
}

// Re-run on dynamic content (e.g., variant change re-renders the block).
const observer = new MutationObserver(() => scan());
observer.observe(document.body, { childList: true, subtree: true });
